/**
 * MindMap - Second Brain Application
 *
 * Main application component with routing and layout
 */

import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ReactFlowProvider } from '@xyflow/react';
import { Toaster } from 'react-hot-toast';

import { useMapStore } from './stores/mapStore';
import { useFeatureAccessStore, FEATURES } from './stores/featureAccessStore';
import { useCoachStore } from './stores/coachStore';
import Layout from './components/ui/Layout';
import DashboardPage from './pages/DashboardPage';
import MindMapPage from './pages/MindMapPage';
import MapPage from './pages/MapPage';
import AuthPage from './pages/AuthPage';
import CashflowPage from './pages/CashflowPage';
import MoodMapPage from './pages/MoodMapPage';
import FitTrackPage from './pages/FitTrackPage';
import FitnessAnalyticsPage from './pages/FitnessAnalyticsPage';
import FeatureLockedPage from './pages/FeatureLockedPage';
import GoogleOAuthCallback from './pages/GoogleOAuthCallback';
import LoadingScreen from './components/ui/LoadingScreen';
import MyCoachesPage from './pages/MyCoachesPage';
import BecomeCoachPage from './pages/coach/BecomeCoachPage';
import CoachDashboardPage from './pages/coach/CoachDashboardPage';
import ClientDetailPage from './pages/coach/ClientDetailPage';
import ProfilePage from './pages/ProfilePage';

// Lazy load admin components
const AdminPage = React.lazy(() => import('./pages/admin/AdminPage'));

// Legacy route redirect component
const LegacyMapRedirect = () => {
  const { mapId } = useParams();
  return <Navigate to={`/mindmap/${mapId}`} replace />;
};

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useMapStore();

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  return children;
};

// Map feature IDs to module names for LoadingScreen
const FEATURE_TO_MODULE = {
  [FEATURES.FITTRACK]: 'fittrack',
  [FEATURES.CASHFLOW]: 'cashflow',
  [FEATURES.MOODMAP]: 'moodmap',
  [FEATURES.MINDMAP]: 'mindmap',
};

// Feature Route wrapper - checks if user has access to a feature
const FeatureRoute = ({ featureId, children }) => {
  const { hasAccess, isLoading } = useFeatureAccessStore();
  const { user } = useMapStore();

  // Initialize feature access if not done
  const { initialize } = useFeatureAccessStore();

  useEffect(() => {
    if (user?.id) {
      initialize(user.id);
    }
  }, [user?.id, initialize]);

  if (isLoading) {
    const moduleName = FEATURE_TO_MODULE[featureId] || 'default';
    return <LoadingScreen module={moduleName} />;
  }

  if (!hasAccess(featureId)) {
    return <FeatureLockedPage featureId={featureId} />;
  }

  return children;
};

// Admin Route wrapper - checks if user is admin
const AdminRoute = ({ children }) => {
  const { isAdmin, isLoading } = useFeatureAccessStore();
  const { isAuthenticated } = useMapStore();

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  if (isLoading) {
    return <LoadingScreen module="admin" />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Coach Route wrapper - checks if user is acting as coach (has clients or pending invitations)
const CoachRoute = ({ children }) => {
  const { isActingAsCoach, isLoadingCoachProfile, hasClients, hasPendingInvitations, checkCoachStatus } = useCoachStore();
  const { isAuthenticated } = useMapStore();
  const [isChecking, setIsChecking] = React.useState(true);

  // Check coach status on mount
  useEffect(() => {
    const check = async () => {
      await checkCoachStatus();
      setIsChecking(false);
    };
    if (isAuthenticated) {
      check();
    }
  }, [isAuthenticated, checkCoachStatus]);

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  if (isLoadingCoachProfile || isChecking) {
    return <LoadingScreen module="coach" />;
  }

  // V2: User is a coach if they have clients or pending invitations
  // Anyone can be invited as coach - no profile required
  if (!isActingAsCoach && !hasClients && !hasPendingInvitations) {
    return <Navigate to="/become-coach" replace />;
  }

  return children;
};

function App() {
  const { initAuth, isAuthenticated, user } = useMapStore();
  const { initialize } = useFeatureAccessStore();
  const { initialize: initCoach } = useCoachStore();
  const [isLoading, setIsLoading] = React.useState(true);

  // Initialize authentication on mount
  useEffect(() => {
    const init = async () => {
      await initAuth();
      setIsLoading(false);
    };
    init();
  }, [initAuth]);

  // Initialize feature access and coach store when user is authenticated
  useEffect(() => {
    if (user?.id) {
      initialize(user.id);
      initCoach();
    }
  }, [user?.id, initialize, initCoach]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <BrowserRouter>
      <ReactFlowProvider>
        <div className="min-h-screen bg-gray-950 text-white">
          <Routes>
            {/* Auth page */}
            <Route
              path="/auth"
              element={
                isAuthenticated ? <Navigate to="/" replace /> : <AuthPage />
              }
            />

            {/* Dashboard - Main hub */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout>
                    <DashboardPage />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* MindMap routes */}
            <Route
              path="/mindmap"
              element={
                <ProtectedRoute>
                  <FeatureRoute featureId={FEATURES.MINDMAP}>
                    <Layout>
                      <MindMapPage />
                    </Layout>
                  </FeatureRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/mindmap/:mapId"
              element={
                <ProtectedRoute>
                  <FeatureRoute featureId={FEATURES.MINDMAP}>
                    <Layout>
                      <MapPage />
                    </Layout>
                  </FeatureRoute>
                </ProtectedRoute>
              }
            />

            {/* Legacy map route - redirect to new path */}
            <Route
              path="/map/:mapId"
              element={<LegacyMapRedirect />}
            />

            {/* CashFlow routes */}
            <Route
              path="/cashflow"
              element={
                <ProtectedRoute>
                  <FeatureRoute featureId={FEATURES.CASHFLOW}>
                    <CashflowPage />
                  </FeatureRoute>
                </ProtectedRoute>
              }
            />

            {/* MoodMap routes */}
            <Route
              path="/moodmap"
              element={
                <ProtectedRoute>
                  <FeatureRoute featureId={FEATURES.MOODMAP}>
                    <MoodMapPage />
                  </FeatureRoute>
                </ProtectedRoute>
              }
            />

            {/* FitTrack routes */}
            <Route
              path="/fittrack"
              element={
                <ProtectedRoute>
                  <FeatureRoute featureId={FEATURES.FITTRACK}>
                    <FitTrackPage />
                  </FeatureRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/fittrack/analytics"
              element={
                <ProtectedRoute>
                  <FeatureRoute featureId={FEATURES.FITTRACK}>
                    <FitnessAnalyticsPage />
                  </FeatureRoute>
                </ProtectedRoute>
              }
            />

            {/* Coach routes - User side */}
            <Route
              path="/coaches"
              element={
                <ProtectedRoute>
                  <MyCoachesPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/become-coach"
              element={
                <ProtectedRoute>
                  <BecomeCoachPage />
                </ProtectedRoute>
              }
            />

            {/* Coach routes - Coach side */}
            <Route
              path="/coach"
              element={
                <ProtectedRoute>
                  <CoachRoute>
                    <CoachDashboardPage />
                  </CoachRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/coach/clients/:clientId"
              element={
                <ProtectedRoute>
                  <CoachRoute>
                    <ClientDetailPage />
                  </CoachRoute>
                </ProtectedRoute>
              }
            />

            {/* Profile route */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />

            {/* Admin routes */}
            <Route
              path="/admin/*"
              element={
                <AdminRoute>
                  <React.Suspense fallback={<LoadingScreen module="admin" />}>
                    <AdminPage />
                  </React.Suspense>
                </AdminRoute>
              }
            />

            {/* OAuth callback */}
            <Route
              path="/auth/google/callback"
              element={
                <ProtectedRoute>
                  <GoogleOAuthCallback />
                </ProtectedRoute>
              }
            />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>

          {/* Toast notifications */}
          <Toaster
            position="bottom-right"
            toastOptions={{
              style: {
                background: '#1f2937',
                color: '#f9fafb',
                border: '1px solid #374151',
              },
              success: {
                iconTheme: {
                  primary: '#10b981',
                  secondary: '#f9fafb',
                },
              },
              error: {
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#f9fafb',
                },
              },
            }}
          />
        </div>
      </ReactFlowProvider>
    </BrowserRouter>
  );
}

export default App;
